import { lighten } from '@mui/material/styles';

export const AML_CARD_HEIGHT = 380;

export const amlCardSx = {
  display: 'flex',
  flexDirection: 'column',
  height: AML_CARD_HEIGHT,
  maxHeight: AML_CARD_HEIGHT,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
};

export const amlCardHeaderSx = {
  px: 2.5,
  pt: 2,
  pb: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  flexShrink: 0,
};

export const amlCardBodySx = {
  px: 2.5,
  pb: 2.5,
  pt: 0,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
};

export const getAmlCardTitleSx = (theme) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
  fontSize: '0.9375rem',
  lineHeight: 1.4,
});

export const getAmlFieldLabelProps = (theme) => ({
  variant: 'body2',
  sx: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    flex: '0 0 auto',
    maxWidth: '45%',
    pr: 2,
  },
});

export const getAmlFieldValueProps = (theme) => ({
  variant: 'body2',
  sx: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'right',
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
});

export const amlSectionCardSx = {
  p: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
};

export const getAmlSectionTitleSx = (theme) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
  fontSize: '0.9375rem',
  lineHeight: 1.4,
});

export const getAmlBodyTextSx = (theme) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
});

export const getAmlPreviewPanelSx = (theme) => ({
  p: 2.5,
  mb: 2.5,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: 1,
  bgcolor: theme.palette.action.hover,
});

export const getAmlPreviewLabelSx = (theme) => ({
  color: theme.palette.text.secondary,
  mb: 0.25,
});

export const getAmlPreviewValueSx = (theme) => ({
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
});

export const getAmlMatchAlertSx = (theme) => ({
  mt: 2.5,
  p: 2.5,
  border: `1px solid ${theme.palette.warning.main}`,
  borderRadius: 1,
  bgcolor: lighten(theme.palette.warning.main, 0.92),
});

export const getAmlMatchAlertTitleSx = (theme) => ({
  color: theme.palette.warning.dark,
  mb: 0.75,
});

export const getAmlMatchAlertBodySx = (theme) => ({
  color: theme.palette.warning.dark,
  lineHeight: 1.6,
});

export const amlButtonSx = {
  textTransform: 'none',
};
// Outer panel
export const amlResultContainerSx = {
  p: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
};

// Header
export const getAmlResultTitleSx = (theme) => ({
  fontWeight: 700,
  fontSize: '1.125rem',
  color: theme.palette.text.primary,
  mb: 0.5,
});

export const getAmlResultSubtitleSx = (theme) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  mb: 2.5,
});

// Pink alert banner (mockup uses pink, not yellow warning)
export const getAmlResultAlertSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  p: 1.5,
  mb: 2.5,
  borderRadius: 1,
  border: `1px solid ${theme.palette.error.light}`,
  bgcolor: lighten(theme.palette.error.main, 0.92),
  color: theme.palette.error.dark,
});

// Metric tiles (Match Score | Case ID)
export const amlMetricBoxSx = {
  flex: 1,
  p: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
};

export const getAmlMetricLabelSx = (theme) => ({
  fontSize: '0.6875rem',
  fontWeight: '500',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  mb: 1,
});

export const getAmlMatchScoreValueSx = (theme) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.error.main,
  lineHeight: 1,
});

// Flagged profile block
export const amlFlaggedProfileSx = {
  mt: 2.5,
  p: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
};

export const getAmlFlaggedLabelSx = (theme) => ({
  fontSize: '0.6875rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  mb: 1,
});

export const getAmlFlaggedNameSx = (theme) => ({
  fontSize: '1.125rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  mb: 1.5,
});

export const getAmlNationalityLabelSx = (theme) => ({
  fontSize: '0.6875rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  mb: 1,
});

export const amlDatasetBadgeSx = {
  display: 'inline-block',
  px: 1.25,
  py: 0.25,
  borderRadius: 10,
  fontSize: '0.75rem',
  bgcolor: 'action.hover',
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'divider',
};

export const amlPepChipSx = {
  height: 'auto',
  py: 0.5,
  fontSize: '0.75rem',
  color: 'text.secondary',
  borderColor: 'divider',
  bgcolor: 'action.hover',
  '& .MuiChip-label': { whiteSpace: 'normal' },
};

// Footer re-run
export const amlResultFooterSx = {
  mt: 3,
  pt: 2,
  borderTop: '1px solid',
  borderColor: 'divider',
};

export const amlReRunButtonSx = {
  textTransform: 'none',
  color: 'text.secondary',
  fontSize: '0.875rem',
  p: 0,
  minWidth: 'auto',
  '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
};
export const amlInitialStateSx = {
  mt: 1,
  py: 6,
  px: 3,
  border: '1px dashed',
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

export const getAmlInitialMessageSx = (theme) => ({
  mt: 2,
  mb: 3,
  maxWidth: 420,
  color: theme.palette.text.secondary,
  fontSize: '0.9375rem',
  lineHeight: 1.6,
});

export const amlInitiateButtonSx = {
  textTransform: 'none',
  px: 3,
  py: 1,
  borderRadius: 1,
  fontWeight: 600,
};
